import type { Ref } from 'react';
import { type MouseEvent, type KeyboardEvent } from 'react';
import type { BoxProps } from '@chakra-ui/react';
import { Box } from '@chakra-ui/react';
import {
  Editor,
  serializePayload,
  useEditorContext,
  type EditorProps,
} from '@halo/ui-chat-react';
import type { SerializePayloadResult } from '@halo/ui-chat-react/components/Editor/serialize/serialize';
import { useRefs } from '@halo/hooks';
import type { Editor as SlateEditor } from 'slate';
import { safeJsonParse } from '@halo/utils';
import { useTranslation } from 'react-i18next';

export interface MessageTextAreaRef {
  send: () => Promise<void>;
}

export interface MessageTextAreaProps extends Omit<EditorProps, 'onChange'> {
  onSend: (result: SerializePayloadResult) => void;
  ref?: Ref<MessageTextAreaRef>;
  onChange?: (result: SerializePayloadResult) => void;
  onValidChange?: (valid: boolean) => void;
  wrapperProps?: BoxProps;
  placeholder?: string;
  focusTrigger?: object;
  editChanged?: boolean;
  isEditing?: boolean;
  sendMessageDisabled?: boolean;
  sendOnEnter?: boolean;
}

export function MessageTextArea({
  onSend,
  onChange,
  onValidChange,
  wrapperProps,
  placeholder,
  focusTrigger,
  ref,
  editChanged,
  isEditing,
  sendMessageDisabled,
  sendOnEnter = true,
  ...props
}: MessageTextAreaProps) {
  const { editor } = useEditorContext();
  const [result, setResult] = useState<SerializePayloadResult>();
  const [sendTrigger, setSendTrigger] = useState<() => void>();
  const { t } = useTranslation();
  const valid = useMemo(() => {
    const isOnlySpace = safeJsonParse(result?.payload as any, {} as any)?.content?.trim() === '';
    if (isOnlySpace) {
      return false;
    }
    return Boolean(!editor.isOverflow() && result?.payload && !/^\s*$/.test(result.payload));
  }, [result?.payload, editor]);

  const onMouseDown = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  const { run: onChange_, flush } = useDebounceFn(
    (editor: SlateEditor) => {
      const result = serializePayload.serialize(editor);
      setResult(result);
      onChange?.(result);
    },
    { wait: 1000 }
  );

  const refs = useRefs({
    onValidChange,
    result,
    valid,
    isEditing,
    editChanged,
    onSend,
    editor,
  });

  const send = useCallback(() => {
    flush();
    return new Promise<void>((resolve) => {
      setSendTrigger(() => resolve);
    });
  }, [flush]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (e.ctrlKey || e.shiftKey) {
            editor.insertBreak();
          } else if (sendOnEnter) {
            send();
          }
          break;
      }
    },
    [send, editor, sendOnEnter]
  );

  useEffect(() => {
    if (focusTrigger) {
      try {
        editor.focus();
      } catch (error) {
        console.error(error);
      }
    }
  }, [editor, focusTrigger]);

  useEffect(() => {
    setTimeout(() => {
      editor.focus();
    }, 500);
  }, [editor]);

  useEffect(() => {
    refs.current.onValidChange?.(valid);
  }, [valid, refs]);

  useEffect(() => {
    if (!sendTrigger) {
      return;
    }

    const { valid, result, isEditing, editChanged, onSend, editor } = refs.current;

    if (!valid || (isEditing && !editChanged)) {
      editor.isOverflow();
    } else {
      onSend(result!);
      editor.clear();
    }

    sendTrigger();
    setSendTrigger(undefined);
  }, [refs, sendTrigger]);

  useImperativeHandle(
    ref,
    () => ({
      send,
    }),
    [send]
  );

  return (
    <Box className="MessageTextArea" flex="1 1 auto" color="fg.subtle" m="auto" {...wrapperProps}>
      {sendMessageDisabled ? (
        <Box>
          <Box>{t('chat.onlyOwnerAndAdminCanStartChatsInGroup')}</Box>
        </Box>
      ) : (
        <Editor
          placeholder={placeholder}
          onMouseDown={onMouseDown}
          onKeyDown={onKeyDown}
          onChange={onChange_}
          {...props}
        />
      )}
    </Box>
  );
}
